import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const lineVariants = {
  hidden: { y: '110%' },
  show: (i) => ({
    y: '0%',
    transition: {
      delay: 0.15 + i * 0.08,
      duration: 1.05,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const orbY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const orbScale = useTransform(scrollYProgress, [0, 1], [1, 1.6]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  // Mouse parallax on orb — desktop pointer devices only
  const orbRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    const onMove = (e) => {
      if (!orbRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 40;
      const y = (e.clientY / window.innerHeight - 0.5) * 40;
      orbRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={SITE.heroSection}
      className="relative min-h-[100svh] w-full overflow-hidden pt-32 pb-20 px-6 md:px-12"
    >
      {/* Layered background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Grid */}
        <motion.div
          style={{ y: gridY }}
          className="absolute inset-0 opacity-[0.18]"
          aria-hidden
        >
          <div
            className="w-full h-full"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
              backgroundSize: '80px 80px',
              maskImage:
                'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }}
          />
        </motion.div>

        {/* Glow orb */}
        <motion.div
          style={{ y: orbY, scale: orbScale }}
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden
        >
          <div
            ref={orbRef}
            className="w-[80vmin] h-[80vmin] rounded-full blur-3xl opacity-70"
            style={{
              background:
                'radial-gradient(circle at 30% 30%, rgba(204,255,0,0.35), rgba(204,255,0,0.05) 45%, transparent 65%)',
              transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}
          />
        </motion.div>

        {/* Second smaller accent */}
        <div
          className="absolute right-[10%] top-[30%] w-[24vmin] h-[24vmin] rounded-full blur-2xl opacity-40"
          style={{
            background:
              'radial-gradient(circle, rgba(204,255,0,0.55), transparent 70%)',
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1600px] mx-auto h-full flex flex-col justify-center min-h-[calc(100svh-8rem)]"
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-8 flex items-center gap-3"
        >
          <span className="inline-block w-2 h-2 bg-[#CCFF00] rounded-full animate-pulse" />
          {t.hero.eyebrow}
        </motion.div>

        {/* Kinetic headline */}
        <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-[-0.03em] text-[10vw] md:text-[7.2vw] lg:text-[6.4vw] max-w-[95%]">
          {t.hero.title.map((line, i) => (
            <span className="reveal-mask" key={i}>
              <motion.span
                custom={i}
                initial="hidden"
                animate="show"
                variants={lineVariants}
                className={`block ${i === t.hero.accentIndex ? 'text-[#CCFF00] italic font-normal' : ''}`}
                style={i === t.hero.accentIndex ? { fontFamily: 'Cabinet Grotesk' } : undefined}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Subtitle + CTA row */}
        <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-5 md:col-start-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="text-base md:text-lg text-white/70 leading-relaxed max-w-xl"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <a
                data-testid={SITE.heroCtaPrimary}
                href="#contact"
                className="group inline-flex items-center gap-3 bg-[#CCFF00] hover:bg-[#B3E600] text-black font-semibold uppercase tracking-wider text-sm px-7 py-4 transition-all hover:shadow-[0_0_40px_-8px_rgba(204,255,0,0.6)]"
              >
                {t.hero.ctaPrimary}
                <ArrowUpRight
                  size={18}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
              <a
                data-testid={SITE.heroCtaSecondary}
                href="#products"
                className="group inline-flex items-center gap-3 border border-white/20 hover:border-white text-white font-semibold uppercase tracking-wider text-sm px-7 py-4 transition-colors"
              >
                {t.hero.ctaSecondary}
                <ArrowDown
                  size={18}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-y-1"
                />
              </a>
            </motion.div>
          </div>
        </div>

        {/* Metrics strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-24 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8"
        >
          {t.hero.metrics.map((m) => (
            <div key={m.v}>
              <div className="font-display text-4xl md:text-5xl font-bold tracking-tighter">
                {m.k}
              </div>
              <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/50 mt-2">
                {m.v}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 z-10"
      >
        <span>{lang === 'tr' ? 'Aşağı Kaydır' : 'Scroll'}</span>
        <span className="w-8 h-px bg-white/40" />
      </motion.div>
    </section>
  );
};
